import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './index.css';

const OtherSectionContentPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // API Configuration
    const BASE_URL = 'https://www.whysocial.in/clap-kartel/public';
    const BANNER_IMAGE_BASE_URL = 'https://whysocial.in/clap-kartel/public/uploads/banners';

    // Get data from navigation state — supports both old and new navigation format
    const {
        categoryId,
        otherId,
        categoryName,
        subcatId,
        innerdata,
        serviceName,
        catName,
        innerCatName,
        sourcePage
    } = location.state || {};

    // Determine which ID to use for API call — subcatId takes priority
    const contentId = subcatId || otherId || categoryId;

    // Is this coming from InnerDataPage? Use a different API endpoint
    const isInnerData = innerdata === 'true';

    // Build display name for breadcrumb
    const displayName = innerCatName || catName || categoryName || 'Content';

    // State for content items
    const [contentItems, setContentItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFallbackList, setIsFallbackList] = useState(false);

    // Video (podcast) state
    const [videos, setVideos] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoIndex, setVideoIndex] = useState(0);
    const [isMuted, setIsMuted] = useState(true);

    // New state for inline playback of a specific clicked media item
    const [inlineVideo, setInlineVideo] = useState(null);

    // Banner state
    const [bannerData, setBannerData] = useState([]);
    const bannerStripRef = useRef(null);

    // Helper: auth headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    // Helper: content image URL
    const getContentImageUrl = (imagePath) => {
        if (!imagePath) return '';
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
        return `${BASE_URL}/${imagePath}`;
    };

    // Helper: banner image URL
    const getBannerImageUrl = (imageName) => {
        if (!imageName) return '';
        if (imageName.startsWith('http://') || imageName.startsWith('https://')) return imageName;
        return `${BANNER_IMAGE_BASE_URL}/${imageName}`;
    };

    // Helper: convert YouTube URL to embed
    const convertToEmbedUrl = (url, muted = true) => {
        if (!url) return '';
        if (url.includes('youtube.com/embed/')) return url;
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
        if (match && match[1]) {
            return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=${muted ? '1' : '0'}&controls=1&modestbranding=1&rel=0`;
        }
        return url;
    };

    // Helper: get YouTube thumbnail
    const getYouTubeThumbnail = (url) => {
        if (!url) return null;
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
        if (match && match[1]) return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
        return null;
    };

    // Helper: is YouTube link?
    const isYouTubeLink = (url) => {
        if (!url) return false;
        return url.includes('youtube.com') || url.includes('youtu.be');
    };

    // Helper: to title case
    const toTitleCase = (text) => {
        if (!text) return '';
        return text.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    // Helper: get file type
    const getFileType = (filePath) => {
        if (!filePath) return null;
        const ext = filePath.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
        if (['pdf'].includes(ext)) return 'pdf';
        return 'file';
    };

    // Fetch all data
    useEffect(() => {
        if (!contentId) {
            setLoading(false);
            return;
        }

        const fetchAll = async () => {
            try {
                setLoading(true);
                setError(null);
                setIsFallbackList(false);

                // Fetch content items
                const apiUrl = isInnerData
                    ? `${BASE_URL}/api/innercontent/${contentId}`
                    : `${BASE_URL}/api/content/${contentId}`;

                const response = await fetch(apiUrl, { method: 'GET', headers: getAuthHeaders() });
                if (!response.ok) throw new Error(`Failed to fetch content: ${response.status}`);
                const result = await response.json();

                let items = [];
                if (result?.content && Array.isArray(result.content)) {
                    items = result.content;
                } else if (result?.data && Array.isArray(result.data)) {
                    items = result.data;
                } else if (Array.isArray(result)) {
                    items = result;
                }

                // Fallback: if empty try get-inner-list
                if (items.length === 0 && !isInnerData) {
                    const fallbackUrl = `${BASE_URL}/api/get-inner-list/${contentId}`;
                    const fallbackResponse = await fetch(fallbackUrl, { method: 'GET', headers: getAuthHeaders() });
                    if (fallbackResponse.ok) {
                        const fallbackResult = await fallbackResponse.json();
                        if (fallbackResult?.data && Array.isArray(fallbackResult.data)) {
                            items = fallbackResult.data.map(item => ({
                                ...item,
                                content_title: item.content_title || item.title_name || ''
                            }));
                        } else if (Array.isArray(fallbackResult)) {
                            items = fallbackResult.map(item => ({
                                ...item,
                                content_title: item.content_title || item.title_name || ''
                            }));
                        }
                        if (items.length > 0) setIsFallbackList(true);
                    }
                }

                setContentItems(items);

                // Fetch videos (podcasts)
                const videoResponse = await fetch(`${BASE_URL}/getPodCastAll`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (videoResponse.ok) {
                    const videoResult = await videoResponse.json();
                    if (videoResult?.result && Array.isArray(videoResult.result) && videoResult.result.length > 0) {
                        setVideos(videoResult.result);
                        setSelectedVideo(videoResult.result[0]);
                    }
                }

                // Fetch banners
                const bannerResponse = await fetch(`${BASE_URL}/dash/getbanners/1`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (bannerResponse.ok) {
                    const bannerResult = await bannerResponse.json();
                    if (bannerResult?.banners && Array.isArray(bannerResult.banners)) {
                        setBannerData(bannerResult.banners);
                    } else if (Array.isArray(bannerResult)) {
                        setBannerData(bannerResult);
                    }
                }

            } catch (err) {
                console.error('Error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [contentId, navigate]);

    // Banner auto-scroll
    useEffect(() => {
        if (bannerData.length === 0) return;
        const interval = setInterval(() => {
            if (bannerStripRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = bannerStripRef.current;
                if (scrollLeft + clientWidth >= scrollWidth - 10) {
                    bannerStripRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    bannerStripRef.current.scrollBy({ left: 200, behavior: 'smooth' });
                }
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [bannerData]);

    // Video nav
    const handlePrevVideo = () => {
        if (!videos.length) return;
        const idx = videoIndex > 0 ? videoIndex - 1 : videos.length - 1;
        setVideoIndex(idx);
        setSelectedVideo(videos[idx]);
    };

    const handleNextVideo = () => {
        if (!videos.length) return;
        const idx = videoIndex < videos.length - 1 ? videoIndex + 1 : 0;
        setVideoIndex(idx);
        setSelectedVideo(videos[idx]);
    };

    // Fallback list item click
    const handleFallbackItemClick = (item) => {
        navigate('/other-section-content', {
            state: {
                subcatId: item.id,
                innerdata: 'true',
                categoryName: item.title_name || item.content_title,
                serviceName,
                innerCatName: item.title_name || item.content_title
            }
        });
    };

    if (loading) {
        return (
            <div className="oscp-page-wrapper">
                <div className="oscp-loading-container">
                    <div className="oscp-loading-spinner"></div>
                    <p>Loading {displayName}...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="oscp-page-wrapper">
                <div className="oscp-loading-container">
                    <p style={{ color: '#e74c3c' }}>Error: {error}</p>
                    <button className="oscp-back-btn" onClick={() => navigate(-1)}>← Go Back</button>
                </div>
            </div>
        );
    }

    // Helper: navigate back to the correct source page
    const handleGoBack = () => {
        if (sourcePage === '/') {
            navigate('/', { state: { activeTab: 'other' } });
        } else {
            navigate(sourcePage || '/other-section');
        }
    };

    if (contentItems.length === 0) {
        return (
            <div className="oscp-page-wrapper">
                {/* Breadcrumb even on empty state */}
                <div className="oscp-breadcrumb-header">
                    <div className="oscp-breadcrumb-content">
                        <span className="oscp-back-arrow" onClick={handleGoBack}>‹</span>
                        <h1 className="oscp-breadcrumb-title">
                            <span className="oscp-breadcrumb-link" onClick={handleGoBack}>Other Sections</span>
                            {serviceName && serviceName.trim() !== '' && (
                                <>
                                    <span className="oscp-breadcrumb-sep">&gt;</span>
                                    <span className="oscp-breadcrumb-link" onClick={handleGoBack}>
                                        {toTitleCase(serviceName)}
                                    </span>
                                </>
                            )}
                            {catName && catName.trim() !== '' && (
                                <>
                                    <span className="oscp-breadcrumb-sep">&gt;</span>
                                    <span className="oscp-breadcrumb-current">{catName.toUpperCase()}</span>
                                </>
                            )}
                        </h1>
                    </div>
                </div>
                <div className="oscp-loading-container" style={{ flexDirection: 'column', gap: '16px', paddingTop: '60px' }}>
                    <div style={{ fontSize: '48px' }}>📋</div>
                    <p style={{ color: '#333', fontWeight: '600', fontSize: '16px', margin: 0 }}>
                        {displayName || catName || 'This section'} — Coming Soon
                    </p>
                    <p style={{ color: '#888', fontSize: '14px', margin: 0, maxWidth: '280px', textAlign: 'center', lineHeight: 1.5 }}>
                        Content for this category is being prepared. Please check back later.
                    </p>
                    <button className="oscp-back-btn" onClick={handleGoBack}>← Go Back</button>
                </div>
            </div>
        );
    }

    // Isolated Inline Video View
    if (inlineVideo) {
        const titleText = inlineVideo.content_title || inlineVideo.title_name || 'Video Player';
        return (
            <div className="oscp-page-wrapper" style={{ background: '#f2f2f2', minHeight: '100vh', padding: 0 }}>
                {/* Header for Inline Video */}
                <div style={{ width: '100%', borderBottom: '1px solid #d1d5db', padding: '15px 20px', display: 'flex', alignItems: 'center', gap: '15px', background: '#f2f2f2' }}>
                    <span
                        style={{ fontSize: '28px', cursor: 'pointer', fontWeight: 'bold', lineHeight: 1, userSelect: 'none' }}
                        onClick={() => setInlineVideo(null)}
                    >
                        ‹
                    </span>
                    <h2 style={{ fontSize: '18px', margin: 0, fontWeight: '700', color: '#000', fontFamily: 'SF Pro Display, Sen, sans-serif' }}>
                        {titleText}
                    </h2>
                </div>

                {/* Video Player Area */}
                <div style={{ width: '100%', maxWidth: '700px', margin: '40px auto', padding: '0 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                    <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: '15px', overflow: 'hidden', backgroundColor: '#000', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <iframe
                            src={convertToEmbedUrl(inlineVideo.link_url, false)}
                            title={titleText}
                            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>

                    <p style={{ marginTop: '20px', fontSize: '15px', color: '#111', fontFamily: '"Sen", sans-serif', width: '100%', textAlign: 'left', fontWeight: '500' }}>
                        {titleText}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="oscp-page-wrapper">
            {/* ===== BREADCRUMB ===== */}
            <div className="oscp-breadcrumb-header">
                <div className="oscp-breadcrumb-content">
                    <span className="oscp-back-arrow" onClick={() => navigate(-1)}>‹</span>
                    <h1 className="oscp-breadcrumb-title">
                        <span className="oscp-breadcrumb-link" onClick={() => {
                            if (sourcePage === '/') {
                                navigate('/', { state: { activeTab: 'other' } });
                            } else {
                                navigate(sourcePage || '/other-section');
                            }
                        }}>Other Sections</span>
                        {serviceName && serviceName.trim() !== '' && (
                            <>
                                <span className="oscp-breadcrumb-sep">&gt;</span>
                                <span className="oscp-breadcrumb-link" onClick={() => navigate(isInnerData ? -2 : -1)}>
                                    {toTitleCase(serviceName)}
                                </span>
                            </>
                        )}
                        {catName && catName.trim() !== '' && (
                            <>
                                <span className="oscp-breadcrumb-sep">&gt;</span>
                                <span className="oscp-breadcrumb-link" onClick={() => navigate(-1)}>
                                    {catName.toUpperCase()}
                                </span>
                            </>
                        )}
                        {displayName && displayName !== catName && displayName.trim() !== '' && (
                            <>
                                <span className="oscp-breadcrumb-sep">&gt;</span>
                                <span className="oscp-breadcrumb-current">{displayName.toUpperCase()}</span>
                            </>
                        )}
                    </h1>
                </div>
            </div>

            {/* ===== TWO-COLUMN LAYOUT ===== */}
            <div className="oscp-two-column-layout">

                {/* LEFT COLUMN — Video + Banners */}
                <div className="oscp-left-column">
                    {/* Video Player */}
                    <div className="oscp-video-container">
                        {selectedVideo ? (
                            <iframe
                                key={`${selectedVideo.id}-${isMuted}`}
                                src={convertToEmbedUrl(selectedVideo.video_url, isMuted)}
                                title={selectedVideo.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="oscp-video-iframe"
                            />
                        ) : (
                            <div className="oscp-video-placeholder">
                                <span>No Video Available</span>
                            </div>
                        )}
                        {videos.length > 1 && (
                            <>
                                <button className="oscp-video-nav oscp-video-nav-left" onClick={handlePrevVideo}>‹</button>
                                <button className="oscp-video-nav oscp-video-nav-right" onClick={handleNextVideo}>›</button>
                            </>
                        )}
                    </div>

                    {/* Banner Strip */}
                    {bannerData.length > 0 && (
                        <div className="oscp-banner-strip" ref={bannerStripRef}>
                            {bannerData.map((banner, index) => {
                                const imgSrc = getBannerImageUrl(banner?.banner_img || banner?.image || banner?.banner_image || banner?.url);
                                return (
                                    <div key={`banner-${index}`} className="oscp-banner-card">
                                        <img
                                            src={imgSrc}
                                            alt={banner?.title || `Banner ${index + 1}`}
                                            className="oscp-banner-img"
                                            onError={(e) => { e.target.style.opacity = '0.3'; }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* RIGHT COLUMN — Content Cards */}
                <div className="oscp-right-column">
                    <div className="oscp-content-card-container">
                        {isFallbackList ? (
                            /* Fallback: show as clickable list */
                            <div className="oscp-fallback-list">
                                {contentItems.map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        className="oscp-fallback-item"
                                        onClick={() => handleFallbackItemClick(item)}
                                    >
                                        <span className="oscp-fallback-name">
                                            {toTitleCase(item.title_name || item.content_title)}
                                        </span>
                                        <span className="oscp-fallback-arrow">&gt;</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* Normal: show media cards */
                            <div className="oscp-media-grid">
                                {contentItems.map((item, index) => {
                                    const hasYouTube = isYouTubeLink(item.link_url);
                                    const ytThumb = hasYouTube ? getYouTubeThumbnail(item.link_url) : null;
                                    const contentImg = getContentImageUrl(item.content_image);

                                    return (
                                        <div
                                            key={item.id || index}
                                            className="oscp-media-card"
                                            onClick={() => {
                                                if (item.link_url) {
                                                    if (isYouTubeLink(item.link_url)) {
                                                        // Open inline player instead of redirecting
                                                        setInlineVideo(item);
                                                    } else {
                                                        // Normal external link
                                                        window.open(item.link_url, '_blank', 'noopener,noreferrer');
                                                    }
                                                }
                                            }}
                                        >
                                            {/* Thumbnail */}
                                            {(ytThumb || contentImg) ? (
                                                <div className="oscp-media-thumb">
                                                    <img
                                                        src={ytThumb || contentImg}
                                                        alt={item.content_title}
                                                        className="oscp-media-thumb-img"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    {hasYouTube && (
                                                        <div className="oscp-play-overlay">
                                                            <div className="oscp-play-icon">▶</div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : null}

                                            {/* Text info */}
                                            <div className="oscp-media-info">
                                                <p className="oscp-media-title">{item.content_title}</p>
                                                {item.content_attached_file && (
                                                    <a
                                                        href={getContentImageUrl(item.content_attached_file)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="oscp-file-link"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        📎 View File
                                                    </a>
                                                )}
                                                {item.content_attached_file_two && (
                                                    <a
                                                        href={getContentImageUrl(item.content_attached_file_two)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="oscp-file-link"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        📄 Download {getFileType(item.content_attached_file_two) === 'pdf' ? 'PDF' : 'File'}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OtherSectionContentPage;
